/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a function, not a React component. */
import { readOwnFlag } from '../table-internals/read-own-flag.js';
import { readCachedChildren } from './read-cached-children.js';

interface ShouldReleaseRequestArgs<TChild> {
  childRowsByParentId: Readonly<Record<string, TChild[] | undefined>>;
  loadingParentIds: Readonly<Record<string, boolean>> | undefined;
  parentId: string;
  hasConsumerReportedFetching: (parentId: string) => boolean;
}

/**
 * Whether a parent's outstanding onExpandRow record should be forgotten.
 *
 * Two ways a request is over: the children arrived, or the consumer reported it fetching and has
 * since stopped — which is how a failed fetch becomes retryable, since a failure never writes the
 * cache. The transition matters rather than the flag's value: a map that merely carries `false`
 * for every id would otherwise release on every toggle and leave the duplicate-fetch guard inert.
 *
 * Both the per-row toggle and expand-all must use this. Expand-all previously implemented only the
 * cached half, which made a failed fetch permanently unretryable through the toolbar while the
 * chevron could retry it from identical state.
 */
export function shouldReleaseRequest<TChild>({
  childRowsByParentId,
  loadingParentIds,
  parentId,
  hasConsumerReportedFetching,
}: ShouldReleaseRequestArgs<TChild>): boolean {
  if (readCachedChildren(childRowsByParentId, parentId) !== undefined) return true;
  return hasConsumerReportedFetching(parentId) && !readOwnFlag(loadingParentIds, parentId);
}
